<template>
  <div
    :class="{ subtab: isSubtab, active }"
    data-cy="side-tab"
    class="tab-container vertical-tab"
    :style="containerStyle"
    @click="handleClick"
  >
    <div
      v-if="isSubtab"
      class="position-absolute sub-tab-border"
      :class="{ active }"
      :style="subTabStyle"
    />
    <div class="tab-inner px-2">
      <TabImage
        v-if="details.showMobileIcon"
        :story-path="details.path.toString()"
        :size="40"
        :fixed-width="true"
        :secondary-app-icon-id="secondaryAppIconId"
      />
      <div v-if="details.showMobileLabel" ref="tabLabel" class="tab-label px-2">
        {{ title }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import { CSSProperties } from 'vue/types/jsx'
import Vue, { PropType } from 'vue'
import TabImage from '@/components/public/SideTabs/TabImage.vue'
import { Tab } from '@/types'
import { VIEW_PUBLIC } from '@/constants'
const components = { TabImage }

const props = {
  height: {
    type: Number,
    required: false,
  },
  width: {
    type: Number,
    required: false,
  },
  details: {
    type: Object as PropType<Tab>,
    required: true,
  },
}

@Component({ components })
export default class CarouselTab extends Vue.extend({ props }) {
  get subTabStyle() {
    return {
      'border-color': this.details.barColour,
    } as CSSProperties
  }

  get containerStyle() {
    const background =
      this.originalTabColours && this.isSecondaryAppTab
        ? this.details.barColour
        : this.details.colour
    return {
      'min-height': this.height ? `${this.height}px` : 'unset',
      'min-width': this.width ? `${this.width}px` : 'unset',
      color: this.details.tabFontColor,
      'background-color': background,
    } as CSSProperties
  }

  get display() {
    return this.$typedStore.public.display
  }

  async handleClick() {
    if (this.details.secondaryAppPath) {
      if (this.details.secondaryAppPath !== this.display.activeTab?.secondaryAppPath) {
        this.display.setSwitchingSecondaryApps(true)
      }

      await this.display.updateSecondaryStoryPath({
        primaryStoryPath: this.details.secondaryAppPath,
        secondaryStoryPath: this.details.path,
        mainpanel: VIEW_PUBLIC,
      })
    } else {
      this.display.updatePrimaryStoryPath(this.details.path)
    }

    this.$analytics.event('click', {
      event_category: 'side-tab',
      event_label: this.details.path,
    })
  }

  get originalTabColours() {
    return this.$typedStore.primary.app.config.appConfig?.originalTabColours ?? false
  }

  get isSecondaryAppTab() {
    return !!this.details.secondaryAppPath
  }

  get secondaryAppIconId() {
    return this.details.secondaryAppPath ? this.details.iconId : null
  }

  get isSubtab() {
    return !!this.details.secondaryAppPath && !this.details.isSecondaryInitialTab
  }

  get active() {
    return this.display.activeTab === this.details
  }

  get title() {
    return this.details.label || this.details.name
  }
}
</script>

<style lang="scss" scoped>
.tab-container {
  .tab-inner {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    .tab-label {
      text-align: center;
      line-height: 1.15;
      font-size: 12px;
    }
  }

  @media (orientation: landscape) {
    max-width: 140px;
    .tab-inner {
      flex-direction: row;
      justify-content: space-between;
      .tab-label {
        flex: 1;
      }
    }
  }
  @media (orientation: portrait) {
    .tab-inner {
      flex-direction: column;
      .tab-label {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
      }
    }
  }
}
</style>
